/* Catalogue building blocks, shared by the main sections of the site.
   ----------------------------------------------------------------------
   Camps & Trips uses them first (src/pages/Trips.jsx and
   src/pages/trips/CampPage.jsx); Programs and Career Pathways &
   Internships are meant to reuse the same parts so the three sections
   look and read the same way.

   Every component takes copy that is already resolved to the visitor's
   language (strings, not { en, ar, ru } triples): resolve with useL()
   in the page. Nothing here reads window during render, so the
   pre-rendered HTML and the first client render always match. */

import { srcSetFor, SIZES } from "../../lib/img";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import LocaleLink from "../LocaleLink";
import { getFirestoreApi } from "../../firebase";
import { cleanField, cleanEmail, cleanPhone, isHoneypotTripped } from "../../lib/formSecurity";
import { track } from "../../lib/analytics";
import "./Catalog.css";

/* Forward arrow; .i-arrow mirrors it in Arabic (index.css). */
export const ARROW = (
  <svg className="i-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* A photo: WebP in three sizes (scripts/image-variants.mjs) with the
   JPEG master as the fallback. `src` is the path without its
   extension; `sizes` says how wide it is drawn (src/lib/img.js). */
export function Pic({ src, alt, eager = false, w = 1600, h = 1067, className, pos, sizes = SIZES.card }) {
  return (
    <picture className={className}>
      <source srcSet={srcSetFor(src)} sizes={sizes} type="image/webp" />
      <img
        src={`${src}.jpg`}
        alt={alt}
        width={w}
        height={h}
        style={pos ? { objectPosition: pos } : undefined}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={eager ? "high" : undefined}
      />
    </picture>
  );
}

/* One item in a grid. The title link stretches over the whole card, so
   the card has a single tab stop and a short accessible name.
   `featured`: the wide card at the top of an index (photo beside the
   text, title as an h2, the "more" line drawn as a button). */
export function CatalogCard({ href, photo, badge, tone = "", eyebrow, title, text, facts = [], more, large = false, featured = false, eager = false }) {
  const TitleTag = featured ? "h2" : "h3";
  const cls = `cat-card${large ? " cat-card--large" : ""}${featured ? " cat-card--featured" : ""}`;
  return (
    <article className={cls}>
      <div className="cat-card-media">
        <Pic src={photo.src} alt={photo.alt} eager={eager} />
        {badge && (
          <p className="cat-card-badge">
            <span className={`cat-pill${tone ? ` cat-pill--${tone}` : ""}`}>{badge}</span>
          </p>
        )}
      </div>
      <div className="cat-card-body">
        {eyebrow && <p className="cat-card-eyebrow">{eyebrow}</p>}
        <TitleTag className="cat-card-title">
          {href.startsWith("#")
            ? <a href={href} className="cat-card-link">{title}</a>
            : <LocaleLink to={href} className="cat-card-link">{title}</LocaleLink>}
        </TitleTag>
        {text && <p className="cat-card-text">{text}</p>}
        {facts.length > 0 && (
          <ul className="cat-card-facts">
            {facts.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        )}
        {more && <span className="cat-card-more" aria-hidden="true">{more} {ARROW}</span>}
      </div>
    </article>
  );
}

/* Key facts in one white bar that wraps into rows; every cell grows to
   fill its row, so there are never empty cells. rows: [{ label, value }] */
export function FactBar({ rows, label }) {
  return (
    <dl className="cat-factbar" aria-label={label}>
      {rows.map((r, i) => (
        <div key={i}>
          <dt>{r.label}</dt>
          <dd>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* A highlighted block with an icon: the educational side of a trip. */
const CAP = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 10 12 5 2 10l10 5 10-5Z" />
    <path d="M6 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5" />
    <path d="M22 10v6" />
  </svg>
);
export function NoteBlock({ title, children }) {
  return (
    <section className="cat-edu">
      <span className="cat-edu-icon">{CAP}</span>
      <div>
        <h2 className="cat-edu-title">{title}</h2>
        <p className="cat-edu-text">{children}</p>
      </div>
    </section>
  );
}

export function CheckList({ items, columns = false }) {
  return (
    <ul className={`cat-check${columns ? " cat-check--two" : ""}`}>
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}

/* Titled short blocks, two per row. items: [{ t, b }] */
export function ThemeList({ items }) {
  return (
    <ul className="cat-themes">
      {items.map((it, i) => (
        <li key={i}>
          <h3>{it.t}</h3>
          <p>{it.b}</p>
        </li>
      ))}
    </ul>
  );
}

/* A day-by-day programme as a timeline: a numbered marker per day,
   joined by a line. days: [{ n, t, b }] */
export function DayList({ days, dayWord }) {
  return (
    <ol className="cat-timeline">
      {days.map((d) => (
        <li key={d.n}>
          <span className="cat-tl-mark" aria-hidden="true">
            <span className="cat-tl-word">{dayWord}</span>
            <span className="cat-tl-n">{d.n}</span>
          </span>
          <div className="cat-tl-body">
            <h3 className="cat-tl-title"><span className="cat-visually-hidden">{dayWord} {d.n}: </span>{d.t}</h3>
            <p className="cat-tl-text">{d.b}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/* Three cards, optionally numbered 01, 02, 03. items: [{ t, b }] */
export function ThreeCards({ items, numbered = false }) {
  return (
    <ol className="cat-three">
      {items.map((it, i) => (
        <li key={i}>
          {numbered && <span className="cat-step-n" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>}
          <h3>{it.t}</h3>
          <p>{it.b}</p>
        </li>
      ))}
    </ol>
  );
}

/* Scroll to the element named in the URL hash after a client-side
   navigation (the header's "School trips" entry links to /trips#…).
   The site-wide ScrollToTop runs first, then this lands on the target;
   sections carry scroll-margin-top so the fixed header never covers it. */
export function useHashScroll() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;
    const id = decodeURIComponent(hash.slice(1));
    let tries = 0;
    let raf = 0;
    const go = () => {
      const el = document.getElementById(id);
      if (!el) {
        if (tries++ < 30) raf = requestAnimationFrame(go);
        return;
      }
      el.scrollIntoView({ block: "start" });
    };
    raf = requestAnimationFrame(go);
    return () => cancelAnimationFrame(raf);
  }, [hash]);
}

/* A call to action that stays on screen once the hero's own buttons have
   scrolled away, and steps aside while the form it points to is in view.
   Starts hidden so the pre-rendered HTML and the first render match. */
export function FloatingCta({ href, label, heroSelector, targetId, event, params }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const hero = document.querySelector(heroSelector);
    const target = document.getElementById(targetId);
    if (!hero || !target || typeof IntersectionObserver === "undefined") return undefined;
    let heroIn = true;
    let targetIn = false;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.target === hero) heroIn = e.isIntersecting;
        if (e.target === target) targetIn = e.isIntersecting;
      }
      setShow(!heroIn && !targetIn);
    });
    io.observe(hero);
    io.observe(target);
    return () => io.disconnect();
  }, [heroSelector, targetId]);
  /* Inside a landmark so assistive technology can reach it (axe "region"). */
  return (
    <aside aria-label={label} hidden={!show}>
      <a href={href} className="btn btn-gold-lg cat-float" onClick={() => event && track(event, params)}>
        {label}
      </a>
    </aside>
  );
}

/* Enquiry form. Writes to the existing `waitlistLeads` collection, whose
   Firestore rules allow exactly { source, name, contact, createdAt }:
     source  = `${sourcePrefix}:${destination}`   e.g. "trips:tokyo-osaka" (≤ 40)
     name    = the visitor's name                  (≤ 80)
     contact = "Phone … · Email … · Org … · Where … · <details>" (≤ 200)
   Values stay English whatever the page language (I18N.md rule).
   copy: resolved strings (see CAMPS_COPY.enquiry in src/data/camps.js).
   options: [{ value, label }]; "elsewhere" is added at the end. */
export function EnquiryForm({ copy, options, defaultValue = "", sourcePrefix, event, idPrefix = "enq" }) {
  const [form, setForm] = useState({ name: "", org: "", phone: "", email: "", destination: defaultValue, where: "", details: "", website: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");
  const [invalid, setInvalid] = useState({});
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const elsewhere = form.destination === "elsewhere";
  const id = (k) => `${idPrefix}-${k}`;

  /* Hydration does not reapply a controlled <select>'s value to the
     pre-rendered DOM, so a preselected destination could show the first
     option while React holds another. Put the DOM in step once on mount.
     (scripts/prerender.mjs also writes the `selected` attribute.) */
  const selectRef = useRef(null);
  useEffect(() => {
    const el = selectRef.current;
    if (el && el.value !== form.destination) el.value = form.destination;
    // Mount only: later changes go through React as usual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (isHoneypotTripped(form.website)) { setStatus("done"); return; }
    const name = cleanField(form.name, 80);
    const org = cleanField(form.org, 60);
    const where = cleanField(form.where, 40);
    const details = cleanField(form.details, 120);
    const phone = cleanPhone(form.phone, 25);
    const email = cleanEmail(form.email, 60);
    const known = form.destination === "elsewhere" || options.some((o) => o.value === form.destination);
    const destination = known ? form.destination : "";
    const bad = {
      name: !name,
      phone: !phone.valid,
      email: !email.valid,
      destination: !destination,
      where: elsewhere && !where,
    };
    setInvalid(bad);
    if (Object.values(bad).some(Boolean)) { setError(copy.errFields); setStatus("error"); return; }

    const contact = [
      `Phone ${phone.value}`,
      `Email ${email.value}`,
      org ? `Org ${org}` : null,
      elsewhere ? `Where ${where}` : null,
      details || null,
    ].filter(Boolean).join(" · ").slice(0, 200);

    setStatus("sending");
    setError("");
    try {
      const { db, collection, addDoc, serverTimestamp } = await getFirestoreApi();
      await addDoc(collection(db, "waitlistLeads"), {
        source: `${sourcePrefix}:${destination}`.slice(0, 40),
        name,
        contact,
        createdAt: serverTimestamp(),
      });
      track("lead_submit", { form: "enquiry", destination });
      if (event) track(event, { destination });
      setStatus("done");
    } catch {
      setError(copy.errSend);
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div className="cat-done" role="status" aria-live="polite">
        <h3>{copy.doneTitle}</h3>
        <p>{copy.doneBody}</p>
      </div>
    );
  }

  const field = (key, label, props = {}, full = false) => (
    <div className={`cat-field${full ? " cat-field--full" : ""}${invalid[key] ? " is-invalid" : ""}`}>
      <label htmlFor={id(key)}>{label}</label>
      <input id={id(key)} value={form[key]} onChange={set(key)} aria-invalid={invalid[key] || undefined} {...props} />
    </div>
  );

  return (
    <form onSubmit={submit} noValidate>
      {/* Spam trap: hidden from people, filled in by bots. */}
      <div className="hp-field" aria-hidden="true">
        <label htmlFor={id("website")}>Website</label>
        <input id={id("website")} type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
      </div>

      <div className="cat-form-grid">
        {field("name", copy.name, { type: "text", autoComplete: "name", maxLength: 80, required: true })}
        {field("org", copy.org, { type: "text", autoComplete: "organization", maxLength: 60 })}
        {field("phone", copy.phone, { type: "tel", autoComplete: "tel", dir: "ltr", maxLength: 25, placeholder: "+971 5X XXX XXXX", required: true })}
        {field("email", copy.email, { type: "email", autoComplete: "email", dir: "ltr", maxLength: 60, placeholder: "you@email.com", required: true })}

        <div className={`cat-field cat-field--full${invalid.destination ? " is-invalid" : ""}`}>
          <label htmlFor={id("destination")}>{copy.destination}</label>
          <select ref={selectRef} id={id("destination")} value={form.destination} onChange={set("destination")} required aria-invalid={invalid.destination || undefined}>
            <option value="" disabled>{copy.choose}</option>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            <option value="elsewhere">{copy.elsewhere}</option>
          </select>
        </div>
        {elsewhere && field("where", copy.elsewhereWhere, { type: "text", maxLength: 40, required: true }, true)}

        <div className="cat-field cat-field--full">
          <label htmlFor={id("details")}>{copy.details}</label>
          <textarea id={id("details")} value={form.details} onChange={set("details")} maxLength={120} rows={3} aria-describedby={id("details-hint")} />
          <span id={id("details-hint")} className="cat-hint">{copy.detailsHint}</span>
        </div>
      </div>

      {status === "error" && <p className="cat-form-error" role="alert">{error}</p>}

      <button type="submit" className="btn btn-gold-lg cat-submit" disabled={status === "sending"}>
        {status === "sending" ? copy.sending : copy.submit}
      </button>
      <p className="cat-form-note">{copy.note}</p>
    </form>
  );
}

/* ── Added for /schools (2026-10-05); section-neutral like the rest ── */

/* A heading with an italic accent: { main, accent }, already resolved.
   The accent is set in the serif italic (upright in Arabic). */
export function Accent({ title }) {
  return <>{title.main} <em className="cat-accent">{title.accent}</em></>;
}

/* Section header: eyebrow, h2 with accent, optional lead. */
export function SectionHead({ id, eyebrow, title, lead, center = false }) {
  return (
    <div className={`cat-head${center ? " cat-head--center" : ""}`}>
      {eyebrow && <p className="cat-eyebrow">{eyebrow}</p>}
      <h2 id={id} className="cat-h2">{title.main ? <Accent title={title} /> : title}</h2>
      {lead && <p className="cat-sub">{lead}</p>}
    </div>
  );
}

/* Numbered steps in one row (four or three). `goal` draws the last step
   in navy, as the destination of the others. items: [{ t, b? }] */
export function StepCards({ items, goal = false, dark = false }) {
  return (
    <ol className={`cat-steps${goal ? " cat-steps--goal" : ""}${dark ? " cat-steps--dark" : ""}`} style={{ "--n": items.length }}>
      {items.map((it, i) => (
        <li key={i}>
          <span className="cat-step-n" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
          <h3>{it.t}</h3>
          {it.b && <p>{it.b}</p>}
        </li>
      ))}
    </ol>
  );
}

/* A card that is not a link: optional photo, pill, title, text, bullets
   and an optional link at the foot. */
export function InfoCard({ title, pill, tone = "", eyebrow, text, bullets = [], photo, link }) {
  return (
    <article className="cat-info">
      {photo && <Pic src={photo.src} alt={photo.alt} className="cat-info-media" />}
      <div className="cat-info-body">
        {pill && <p className="cat-info-pill"><span className={`cat-pill${tone ? ` cat-pill--${tone}` : ""}`}>{pill}</span></p>}
        {eyebrow && <p className="cat-card-eyebrow">{eyebrow}</p>}
        <h3 className="cat-info-title">{title}</h3>
        {text && <p className="cat-info-text">{text}</p>}
        {bullets.length > 0 && <ul className="cat-card-facts">{bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>}
        {link && <LocaleLink to={link.href} className="cat-info-link">{link.label} {ARROW}</LocaleLink>}
      </div>
    </article>
  );
}

/* A table that becomes stacked cards on phones (each cell carries its
   column name). head: [labels], rows: [[cells]] */
export function DataTable({ head, rows, caption }) {
  return (
    <div className="cat-table-wrap">
      <table className="cat-table">
        {caption && <caption>{caption}</caption>}
        <thead><tr>{head.map((h, i) => <th key={i} scope="col">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (j === 0
                ? <th key={j} scope="row" data-label={head[j]}>{c}</th>
                : <td key={j} data-label={head[j]}>{c}</td>))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* Questions and answers as native disclosure widgets (keyboard and
   screen reader support built in, readable without JavaScript). */
export function FaqList({ items }) {
  return (
    <div className="cat-faq">
      {items.map((it, i) => (
        <details key={i}>
          <summary>{it.q}</summary>
          <p>{it.a}</p>
        </details>
      ))}
    </div>
  );
}

/* Contact lines beside an enquiry form. contacts: [{ k, v, href?, ltr? }],
   resolved; `ltr` keeps a phone number left to right in Arabic. */
export function ContactList({ contacts }) {
  return (
    <ul className="cat-contact">
      {contacts.map((k, i) => (
        <li key={i}>
          <span className="cat-contact-k">{k.k}</span>
          {k.href
            ? <a className="cat-contact-v" href={k.href} target={k.href.startsWith("http") ? "_blank" : undefined} rel={k.href.startsWith("http") ? "noopener noreferrer" : undefined}><bdi dir={k.ltr ? "ltr" : undefined}>{k.v}</bdi></a>
            : <span className="cat-contact-v">{k.v}</span>}
        </li>
      ))}
    </ul>
  );
}

/* Partnership enquiry (schools, academies, brands). Writes the same
   document as the home page "For Partners" form, so the live
   `partnershipLeads` rule accepts it unchanged:
     { kind: "partnership", recipient: "partnership@kidsology.ae",
       contactName, organisation, role, email, phone, partnerType,
       message, createdAt }
   The chosen interests lead the message ("Interested in: …"), so they
   reach the team without a rules change. Values stay English.
   copy: resolved strings; options: [{ value (English), label }]. */
export function PartnerEnquiryForm({ copy, options, partnerType = "school", event, idPrefix = "pe" }) {
  const [form, setForm] = useState({ name: "", org: "", role: "", email: "", phone: "", message: "", website: "" });
  /* A form with a single option starts with it ticked. */
  const [picked, setPicked] = useState(options.length === 1 ? [options[0].value] : []);
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");
  const [invalid, setInvalid] = useState({});
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (v) => setPicked((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));
  const id = (k) => `${idPrefix}-${k}`;

  const submit = async (e) => {
    e.preventDefault();
    if (isHoneypotTripped(form.website)) { setStatus("done"); return; }
    const name = cleanField(form.name, 80);
    const org = cleanField(form.org, 120);
    const role = cleanField(form.role, 80);
    const email = cleanEmail(form.email);
    const phoneRaw = cleanField(form.phone, 40);
    const phone = phoneRaw ? cleanPhone(phoneRaw) : { value: "", valid: true };
    const message = cleanField(form.message, 1700);
    const bad = { name: !name, org: !org, email: !email.valid, phone: !phone.valid, message: !message };
    setInvalid(bad);
    if (Object.values(bad).some(Boolean)) { setError(copy.errFields); setStatus("error"); return; }
    if (!picked.length) { setError(copy.errInterests); setStatus("error"); return; }

    const chosen = options.filter((o) => picked.includes(o.value)).map((o) => o.value);
    setStatus("sending");
    setError("");
    try {
      const { db, collection, addDoc, serverTimestamp } = await getFirestoreApi();
      await addDoc(collection(db, "partnershipLeads"), {
        kind: "partnership",
        recipient: "partnership@kidsology.ae",
        contactName: name,
        organisation: org,
        role,
        email: email.value,
        phone: phone.value,
        partnerType,
        message: `Interested in: ${chosen.join(", ")}\n\n${message}`.slice(0, 2000),
        createdAt: serverTimestamp(),
      });
      track("lead_submit", { form: "partner_enquiry", partner_type: partnerType });
      if (event) track(event, { interests: chosen.join(",").slice(0, 100) });
      setStatus("done");
    } catch {
      setError(copy.errSend);
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div className="cat-done" role="status" aria-live="polite">
        <h3>{copy.doneTitle}</h3>
        <p>{copy.doneBody}</p>
      </div>
    );
  }

  const field = (key, label, props = {}, full = false) => (
    <div className={`cat-field${full ? " cat-field--full" : ""}${invalid[key] ? " is-invalid" : ""}`}>
      <label htmlFor={id(key)}>{label}</label>
      <input id={id(key)} value={form[key]} onChange={set(key)} aria-invalid={invalid[key] || undefined} {...props} />
    </div>
  );

  return (
    <form onSubmit={submit} noValidate>
      <div className="hp-field" aria-hidden="true">
        <label htmlFor={id("website")}>Website</label>
        <input id={id("website")} type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
      </div>
      <div className="cat-form-grid">
        {field("name", copy.name, { type: "text", autoComplete: "name", maxLength: 80, required: true })}
        {field("org", copy.school, { type: "text", autoComplete: "organization", maxLength: 120, required: true })}
        {field("role", copy.role, { type: "text", autoComplete: "organization-title", maxLength: 80 })}
        {field("email", copy.email, { type: "email", autoComplete: "email", dir: "ltr", maxLength: 200, placeholder: "you@school.ae", required: true })}
        {field("phone", copy.phone, { type: "tel", autoComplete: "tel", dir: "ltr", maxLength: 40, placeholder: "+971 5X XXX XXXX" }, true)}

        <fieldset className="cat-field cat-field--full cat-choices">
          <legend>{copy.interests} <span className="cat-hint-inline">{copy.interestsHint}</span></legend>
          <div className="cat-choice-grid">
            {options.map((o) => (
              <label key={o.value} className={`cat-choice${picked.includes(o.value) ? " is-checked" : ""}`}>
                <input type="checkbox" checked={picked.includes(o.value)} onChange={() => toggle(o.value)} />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className={`cat-field cat-field--full${invalid.message ? " is-invalid" : ""}`}>
          <label htmlFor={id("message")}>{copy.message}</label>
          <textarea id={id("message")} value={form.message} onChange={set("message")} maxLength={1700} rows={4} required aria-invalid={invalid.message || undefined} />
        </div>
      </div>

      {status === "error" && <p className="cat-form-error" role="alert">{error}</p>}

      <button type="submit" className="btn btn-gold-lg cat-submit" disabled={status === "sending"}>
        {status === "sending" ? copy.sending : copy.submit}
      </button>
      <p className="cat-form-note">{copy.note}</p>
    </form>
  );
}
